import buy from './buy';
import sell from './sell';
import httpClient from '../httpClient';
import {
  Question,
  QuestionFilterCount,
} from 'router/subrouters/DataRoom/models/QAndA';
import { PaginatedData } from 'models/PaginatedData';
import { Params } from 'react-router-dom';
import { AxiosRequestConfig } from 'axios';
import { DropzoneFile } from 'components/Dropzone/utils';
import { getFileParams, getUploadedFile } from 'uploadFileUtils';
import { FilePreview } from 'models/File';

function getQuestions(dataRoomId: string, params: Params) {
  return httpClient.get<PaginatedData<Question>>(
    `data-room-questions/data-rooms/${dataRoomId}`,
    {
      params,
    },
  );
}

function approveQuestion(questionId: string) {
  return httpClient.post(`/data-room-questions/${questionId}/approve-question`);
}

function rejectQuestion(questionId: string, rejectQuestionRespond?: string) {
  return httpClient.post(`/data-room-questions/${questionId}/reject-question`, {
    rejectQuestionRespond,
  });
}

function assignQuestion(
  questionId: string,
  assignedUserIds?: string[] | string,
) {
  return httpClient.post(`/data-room-questions/${questionId}/assign-question`, {
    assignedUserIds,
  });
}

function askAQuestion(
  dataRoomId: string,
  question: Question,
  relatedNodeId?: string,
) {
  return httpClient.post<Question>(`data-room-questions`, {
    ...question,
    dataRoomId,
    dataRoomNodeId: relatedNodeId,
  });
}

function getFitlerCounts(dataRoomId: string) {
  return httpClient.get<QuestionFilterCount>(
    `data-room-questions/data-rooms/${dataRoomId}/filter-counts`,
  );
}

function postFollowUpQuestion(questionId: string, question: Partial<Question>) {
  return httpClient.post(
    `data-room-questions/follow-ups/${questionId}`,
    question,
  );
}

function getFollowUpQuestions(
  dataRoomParentQuestionId: string,
  createdAt: Date,
  params: Params,
) {
  return httpClient.get<PaginatedData<Question>>(
    `data-room-questions/follow-ups/${dataRoomParentQuestionId}`,
    { params: { ...params, createdAt: { $lte: createdAt } } },
  );
}

async function uploadQuestionFile(
  file: File,
  dataRoomId: string,
  config?: AxiosRequestConfig,
): Promise<DropzoneFile | null> {
  const fileParams = getFileParams('qaFile', file);

  const { data } = await httpClient.post<any>(
    `data-room-questions/data-rooms/${dataRoomId}/upload-temporary-object`,
    fileParams,
  );

  return await getUploadedFile(data, file, config);
}

const moveQuestionFileToDataRoom = (questionId: string, fileId: string) => {
  return httpClient.post(
    `data-room-questions/${questionId}/files/${fileId}/move-to-data-room`,
  );
};

const previewQuestionFile = (questionId: string, fileId: string) => {
  return httpClient.get<FilePreview>(
    `data-room-questions/${questionId}/files/${fileId}/preview`,
  );
};

const removeQuestionFile = (
  questionId: string,
  fileId: string,
  deleteFromDataRoom?: boolean,
) => {
  return httpClient.post(
    `data-room-questions/${questionId}/files/${fileId}/delete-file`,
    {
      deleteFromDataRoom,
    },
  );
};

async function uploadAnswerFile(
  file: File,
  dataRoomId: string,
  config?: AxiosRequestConfig,
): Promise<DropzoneFile | null> {
  const fileParams = getFileParams('qaFile', file);

  const { data } = await httpClient.post<any>(
    `data-room-answers/data-rooms/${dataRoomId}/upload-temporary-object`,
    fileParams,
  );

  return await getUploadedFile(data, file, config);
}

const moveAnswerFileToDataRoom = (answerId: string, fileId: string) => {
  return httpClient.post(
    `data-room-answers/${answerId}/files/${fileId}/move-to-data-room`,
  );
};

const removeAnswerFileFromDataRoom = (answerId: string, fileId: string) => {
  return httpClient.post(
    `data-room-answers/${answerId}/files/${fileId}/remove-from-data-room`,
  );
};

const previewAnswerFile = (answerId: string, fileId: string) => {
  return httpClient.get<FilePreview>(
    `data-room-answers/${answerId}/files/${fileId}/preview`,
  );
};

const removeAnswerFile = (
  answerId: string,
  fileId: string,
  deleteFromDataRoom?: boolean,
) => {
  return httpClient.post(
    `data-room-answers/${answerId}/files/${fileId}/delete-file`,
    {
      deleteFromDataRoom,
    },
  );
};

const getQuestion = (questionId: string) => {
  return httpClient.get<Question>(`data-room-questions/${questionId}`);
};

const removeQuestionFileFromDataRoom = (questionId: string, fileId: string) => {
  return httpClient.post(
    `data-room-questions/${questionId}/files/${fileId}/remove-from-data-room`,
  );
};

export default {
  ...buy,
  ...sell,
  askAQuestion,
  getFitlerCounts,
  getQuestions,
  approveQuestion,
  rejectQuestion,
  assignQuestion,
  postFollowUpQuestion,
  getFollowUpQuestions,
  uploadQuestionFile,
  moveQuestionFileToDataRoom,
  previewQuestionFile,
  removeQuestionFile,
  uploadAnswerFile,
  moveAnswerFileToDataRoom,
  removeAnswerFileFromDataRoom,
  previewAnswerFile,
  removeAnswerFile,
  getQuestion,
  removeQuestionFileFromDataRoom,
};
